import React, { useState } from 'react';
import type { AdminTransferDetail } from '@platform/shared-types';
import DatePicker from '../../../components/DatePicker';
import { businessTransferApi } from '../../../services/businessTransferApi';
import { toastService } from '../../../services/toastService';
import { money, when } from '../format';

type P = { detail: AdminTransferDetail; canAct: boolean; onChange: (d: AdminTransferDetail) => void };

const TIMES = ['21:30', '22:00', '22:30', '23:00', '23:30', '00:30', '01:00'];

export default function SchedulePanel({ detail, canAct, onChange }: P) {
  const t = detail.transfer;
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState('22:30');
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<string[]>([]);
  const normalizedPhrase = phrase.trim().toUpperCase();
  const phraseOk = normalizedPhrase === detail.confirmPhrase;

  const run = async (fn: () => Promise<AdminTransferDetail>, ok: string) => {
    setBusy(true); setPending([]);
    try { onChange(await fn()); toastService.success(ok); }
    catch (e: any) { setPending(e.pending || []); toastService.error(e.message); }
    finally { setBusy(false); }
  };

  if (t.run) return <div className="text-sm text-stone-600">Started {when(t.run.startedAt)} by {t.run.startedBy}.</div>;
  return (
    <div className="flex flex-col gap-3 text-sm">
      {t.runAt && <div className="rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-cyan-900">Booked for {when(t.runAt)}. Tills show a notice 15 minutes before.</div>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div><div className="mb-1 text-xs text-stone-500">Date (store local time)</div><DatePicker value={date} onChange={setDate} /></div>
        <div>
          <div className="mb-1 text-xs text-stone-500">Time</div>
          <div className="flex flex-wrap gap-1.5">
            {TIMES.map((x) => (
              <button key={x} type="button" onClick={() => setTime(x)} className={`min-h-[44px] rounded-lg px-3 font-mono text-xs ${time === x ? 'bg-[#9F1239] text-white' : 'border border-stone-300 bg-white text-stone-700'}`}>{x}</button>
            ))}
          </div>
        </div>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        {t.mode === 'store' && <><dt className="text-stone-500">Seller pay-off invoice</dt><dd className="font-mono">{money(detail.payoffCents)}</dd></>}
        {t.mode === 'store' && <><dt className="text-stone-500">Buyer takes over</dt><dd className="font-mono">{money(detail.takeoverMonthlyCents)}/mo</dd></>}
        <dt className="text-stone-500">Card payments</dt><dd className="text-rose-800">Paused from the run until every device is linked</dd>
      </dl>
      <label className="flex flex-col gap-1">
        <span className="text-xs text-stone-500">Type <b className="font-mono text-stone-900">{detail.confirmPhrase}</b> to confirm</span>
        <input value={phrase} onChange={(e) => setPhrase(e.target.value)} className="min-h-[44px] rounded-lg border border-stone-300 px-3 font-mono focus:border-[#DC143C] focus:outline-none focus:ring-2 focus:ring-rose-200" placeholder={detail.confirmPhrase} autoComplete="off" />
      </label>
      {pending.length > 0 && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">Still to finish: {pending.join(', ')}</div>}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={!canAct || !phraseOk || busy} className="min-h-[44px] rounded-lg border border-stone-300 bg-white px-4 font-semibold disabled:opacity-40"
          onClick={() => run(() => businessTransferApi.schedule(t.transferId, { runAt: new Date(`${date}T${time}:00`).getTime(), confirmPhrase: normalizedPhrase }), 'Transfer scheduled')}>Schedule for {time}</button>
        <button type="button" disabled={!canAct || !phraseOk || busy} className="min-h-[44px] rounded-lg bg-[#DC143C] px-4 font-semibold text-white disabled:opacity-40"
          onClick={() => run(() => businessTransferApi.runNow(t.transferId, { confirmPhrase: normalizedPhrase }), 'Transfer started')}>Run now</button>
      </div>
    </div>
  );
}
